/**
 * Migration Script: Update backup_path to use scheduled date from original_path
 * 
 * This script updates all existing backup_path values in the document_backups table
 * to use the scheduled date from original_path instead of the version creation timestamp.
 * 
 * Usage:
 * 1. Import this function in a component or run it from browser console
 * 2. Call: migrateBackupPaths()
 * 
 * IMPORTANT: This script will:
 * - Update database records with new backup_path values
 * - Does NOT move files in storage (files remain in old locations)
 * - Files will need to be manually moved or accessed via old paths if needed
 */

import { getSupabaseAdmin } from "../components/supabaseClient";
import { documentBackupTableName, supabaseServiceKey } from "../components/env";

/**
 * Extract scheduled date from original_path
 * @param {string} originalPath - The original file path (e.g., "New/MedADocs/2025/2025-11-12/file.json")
 * @returns {Object|null} - Object with {year, scheduledDate} or null if not found
 */
function extractScheduledDate(originalPath) {
  if (!originalPath) return null;

  const pathParts = originalPath.split("/");
  
  // Look for a path segment matching YYYY-MM-DD format
  for (const part of pathParts) {
    const dateMatch = part.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch) {
      const year = dateMatch[1];
      const scheduledDate = part;
      return { year, scheduledDate };
    }
  }
  
  return null;
}

/**
 * Generate new backup path using scheduled date from original_path
 * @param {string} originalPath - The original file path
 * @param {string} oldBackupPath - The existing backup path
 * @param {string} timestamp - The backup timestamp
 * @returns {string|null} - New backup path or null if cannot be generated
 */
function generateNewBackupPath(originalPath, oldBackupPath, timestamp) {
  // Extract scheduled date from original_path
  const dateInfo = extractScheduledDate(originalPath);
  if (!dateInfo) {
    console.warn("⚠️ Could not extract scheduled date from original_path:", originalPath);
    return null;
  }

  const { year, scheduledDate } = dateInfo;

  // Extract patient info and version timestamp from old backup path
  // Old format: {year}/{year}-{month}-{day}/{patientName}-{patientId}/v{timestamp}.json
  // New format: {year}/{scheduledDate}/{patientName}-{patientId}/v{timestamp}.json
  const pathMatch = oldBackupPath.match(/^(\d{4})\/\d{4}-\d{2}-\d{2}\/(.+)\/v(.+)\.json$/);
  
  if (!pathMatch) {
    console.warn("⚠️ Could not parse old backup path:", oldBackupPath);
    return null;
  }

  const [, oldYear, patientFolder, versionTimestamp] = pathMatch;
  
  // Use the year from scheduled date, but keep patient folder and version timestamp
  return `${year}/${scheduledDate}/${patientFolder}/v${versionTimestamp}.json`;
}

/**
 * Migrate all backup paths in the database
 * @param {Object} options - Migration options
 * @param {boolean} options.dryRun - If true, only log changes without updating database
 * @param {number} options.batchSize - Number of records to process at a time (default: 100)
 * @param {Function} options.onProgress - Callback function called with progress info
 * @returns {Promise<Object>} - Migration results
 */
export async function migrateBackupPaths(options = {}) {
  const {
    dryRun = false,
    batchSize = 100,
    onProgress = null,
  } = options;

  console.log("🔄 Starting backup path migration...");
  console.log(`Mode: ${dryRun ? "DRY RUN (no changes will be made)" : "LIVE (will update database)"}`);

  // Guard: Check if Supabase config is fully loaded before proceeding
  if (!supabaseServiceKey) {
    throw new Error("Supabase configuration not loaded. Make sure loadConfig() has been called.");
  }

  try {
    const supabaseAdmin = getSupabaseAdmin();
    
    // Get total count of records
    const { count, error: countError } = await supabaseAdmin
      .from(documentBackupTableName)
      .select("*", { count: "exact", head: true });

    if (countError) {
      throw new Error(`Failed to count records: ${countError.message}`);
    }

    console.log(`📊 Total records to process: ${count}`);

    let processed = 0;
    let updated = 0;
    let skipped = 0;
    let errors = 0;
    const errorDetails = [];

    // Process in batches
    let offset = 0;
    while (offset < count) {
      // Fetch batch of records
      const { data: records, error: fetchError } = await supabaseAdmin
        .from(documentBackupTableName)
        .select("id, original_path, backup_path, backup_timestamp")
        .order("id", { ascending: true })
        .range(offset, offset + batchSize - 1);

      if (fetchError) {
        throw new Error(`Failed to fetch records: ${fetchError.message}`);
      }

      if (!records || records.length === 0) {
        break;
      }

      // Process each record
      const updates = [];
      for (const record of records) {
        processed++;

        try {
          // Generate new backup path
          const newBackupPath = generateNewBackupPath(
            record.original_path,
            record.backup_path,
            record.backup_timestamp
          );

          if (!newBackupPath) {
            skipped++;
            console.warn(`⚠️ Skipped record ${record.id}: Could not generate new path`);
            continue;
          }

          // Check if path actually changed
          if (record.backup_path === newBackupPath) {
            skipped++;
            continue;
          }

          // Add to updates batch
          updates.push({
            id: record.id,
            oldPath: record.backup_path,
            newPath: newBackupPath,
          });

          if (onProgress) {
            onProgress({
              processed,
              total: count,
              updated,
              skipped,
              errors,
              current: record.id,
            });
          }
        } catch (error) {
          errors++;
          errorDetails.push({
            id: record.id,
            error: error.message,
          });
          console.error(`❌ Error processing record ${record.id}:`, error);
        }
      }

      // Apply updates if not dry run
      if (updates.length > 0 && !dryRun) {
        // Update records one by one (Supabase doesn't support bulk update with different values)
        for (const update of updates) {
          const { error: updateError } = await supabaseAdmin
            .from(documentBackupTableName)
            .update({ backup_path: update.newPath })
            .eq("id", update.id);

          if (updateError) {
            errors++;
            errorDetails.push({
              id: update.id,
              error: `Update failed: ${updateError.message}`,
            });
            console.error(`❌ Failed to update record ${update.id}:`, updateError);
          } else {
            updated++;
            console.log(`✅ Updated record ${update.id}:`);
            console.log(`   Old: ${update.oldPath}`);
            console.log(`   New: ${update.newPath}`);
          }
        }
      } else if (updates.length > 0 && dryRun) {
        // Just log what would be updated
        for (const update of updates) {
          updated++;
          console.log(`📝 Would update record ${update.id}:`);
          console.log(`   Old: ${update.oldPath}`);
          console.log(`   New: ${update.newPath}`);
        }
      }

      offset += batchSize;

      // Log progress
      console.log(`📊 Progress: ${processed}/${count} processed, ${updated} updated, ${skipped} skipped, ${errors} errors`);
    }

    const results = {
      total: count,
      processed,
      updated,
      skipped,
      errors,
      errorDetails,
      dryRun,
    };

    console.log("✅ Migration completed!");
    console.log("📊 Results:", results);

    return results;
  } catch (error) {
    console.error("❌ Migration failed:", error);
    throw error;
  }
}

/**
 * Helper function to run migration from browser console
 * Usage: await migrateBackupPaths({ dryRun: true }) // Test first
 *        await migrateBackupPaths({ dryRun: false }) // Actually migrate
 */
export default migrateBackupPaths;

